import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  Collapse, IconButton, Button, ToggleButton, ToggleButtonGroup, TablePagination, Alert, CircularProgress,
} from '@mui/material';
import {
  SmartToy as AiIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Flag as FlagIcon,
  CheckCircle as ReviewedIcon,
} from '@mui/icons-material';
import aiAssistantService from '../../services/ai/aiAssistant.service';
import { getBrandColor, getBrandedTableHeadSx } from '../../utils/colorUtils';

const PAGE_SIZE = 20;
const STATUS_COLOR = { PENDING: 'warning', REVIEWED: 'success' };
const errorText = (err) => err?.response?.data?.error || err?.response?.data?.message || 'Something went wrong';
const shortDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const AiCorrections = () => {
  const brandColor = getBrandColor();
  const headSx = getBrandedTableHeadSx();

  const [status, setStatus] = useState('PENDING');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ content: [], total_elements: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await aiAssistantService.getCorrections({ status, page, size: PAGE_SIZE }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [status, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkReviewed = async (id) => {
    setSavingId(id);
    setError(null);
    try {
      const updated = await aiAssistantService.markCorrectionReviewed(id);
      setData(prev => ({
        ...prev,
        content: status === 'PENDING'
          ? prev.content.filter(c => c.id !== id)
          : prev.content.map(c => (c.id === id ? updated : c)),
        total_elements: status === 'PENDING' ? Math.max(0, prev.total_elements - 1) : prev.total_elements,
      }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSavingId(null);
    }
  };

  const corrections = data?.content || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AiIcon sx={{ color: brandColor }} />
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>AI Corrections</Typography>
          <Chip label={`${data?.total_elements || 0} ${status ? status.toLowerCase() : 'total'}`} size="small"
            color={status === 'PENDING' && data?.total_elements ? 'warning' : 'default'} sx={{ fontSize: '0.7rem', height: 22 }} />
          {loading && <CircularProgress size={16} />}
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={status}
          onChange={(e, v) => { if (v !== null) { setStatus(v); setPage(0); setExpanded(null); } }}
          sx={{
            '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', px: 1.5, py: 0.25 },
            '& .Mui-selected': { color: `${brandColor} !important`, bgcolor: `${brandColor}14 !important`, borderColor: `${brandColor} !important` },
          }}
        >
          <ToggleButton value="PENDING">Pending</ToggleButton>
          <ToggleButton value="REVIEWED">Reviewed</ToggleButton>
          <ToggleButton value="">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 2 }}>
        Answers your staff flagged with "Report Issue". Read the AI's answer and the feedback, fix the underlying data or
        setting if needed, then mark the report as reviewed.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button size="small" onClick={load}>Retry</Button>}>{error}</Alert>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={headSx}>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, width: 40 }} />
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Reported by</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Tool</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Feedback</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {corrections.map(c => (
                <React.Fragment key={c.id}>
                  <TableRow hover onClick={() => setExpanded(expanded === c.id ? null : c.id)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <IconButton size="small">
                        {expanded === c.id ? <CollapseIcon sx={{ fontSize: 18 }} /> : <ExpandIcon sx={{ fontSize: 18 }} />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{shortDate(c.created_at)}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{c.user_name || 'Unknown user'}</TableCell>
                    <TableCell>
                      {c.tool_name ? (
                        <Chip label={c.tool_name.replace(/_/g, ' ')} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'action.selected' }} />
                      ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography noWrap sx={{ fontSize: '0.8rem' }}>{c.human_feedback}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={c.status} size="small" color={STATUS_COLOR[c.status] || 'default'} sx={{ fontSize: '0.65rem', height: 20 }} />
                    </TableCell>
                    <TableCell align="center">
                      {c.status === 'PENDING' && (
                        <Button
                          size="small"
                          disabled={savingId === c.id}
                          startIcon={savingId === c.id ? <CircularProgress size={12} /> : <ReviewedIcon sx={{ fontSize: 14 }} />}
                          onClick={(e) => { e.stopPropagation(); handleMarkReviewed(c.id); }}
                          sx={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 0 }}
                        >
                          Mark reviewed
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, border: expanded === c.id ? undefined : 'none' }}>
                      <Collapse in={expanded === c.id} unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>AI answer</Typography>
                          <Paper sx={{ p: 1.5, mb: 1.5 }}>
                            <Typography sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{c.ai_output || 'No AI answer captured'}</Typography>
                          </Paper>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>Feedback</Typography>
                          <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'warning.light' }}>
                            <Typography sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{c.human_feedback}</Typography>
                          </Paper>
                          {c.reviewed_at && (
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 1 }}>
                              Reviewed {c.reviewed_by_name ? `by ${c.reviewed_by_name} ` : ''}on {shortDate(c.reviewed_at)}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {!loading && corrections.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <FlagIcon sx={{ fontSize: 32, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                      {status === 'PENDING' ? 'No reports waiting for review' : 'No reports'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data?.total_elements || 0}
          page={page}
          onPageChange={(e, p) => { setPage(p); setExpanded(null); }}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </Paper>
    </Box>
  );
};

export default AiCorrections;
